// React and React-related imports
import { useState, useEffect, useMemo } from "react";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

// React Router
import { useNavigate, useParams } from "react-router-dom";

// Styled-components
import styled, { useTheme } from "styled-components";

// Custom Hooks and Utilities
import { usePermissions } from "../../hooks/usePermissions";
import { getDateFormat } from "../../utils/date-format";
import { useQueryFilter } from "../../Monolith-UI/QueryFilter/QueryFilter.js";

// API and Components
import { StorageAPI } from "@/Features/Storage/storage.api";
import CaseStorageColumnsDefs from "../../components/CaseStorage/CaseStorageColumnsDefs.js";
import CaseStorageDimensions from "../../components/CaseStorage/CaseStorageDimensions.js";
import Loader from "../../components/Loader.js";
import synchronizeColumnState from "../../utils/synchronize-column-state.js";

// UI Components and Libraries
import Table, { Column, useTable } from "../../Monolith-UI/Table/Table.js";
import ComboButton from "../../Monolith-UI/ComboButton/ComboButton.js";
import { addCustomFieldsToColumnDefs } from "../../components/Evidence/CustomFieldCell.js";
import { CreateStorageModal } from "@/Features/Storage/Modals/CreateStorageModal";
import { CreateStorageResult } from "@/Features/Storage/Forms/CreateStorageForm";
import { StorageFlyout } from "@/Features/Storage/Flyout/StorageFlyout";
import { StorageItem } from "@/Features/Storage/Tables/types";
import { storageQueryKeys } from "@/Features/Storage/storage.query";
import {
  Button as MonolithButton,
  Input,
  Modal,
  Typography,
} from "@monolith-forensics/monolith-ui";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";

// Material-UI Icons
import {
  ViewColumnOutlined as ViewColumnOutlinedIcon,
  FileDownloadOutlined as FileDownloadOutlinedIcon,
  FilterListOff as FilterListOffIcon,
  ZoomOutMapOutlined as ZoomOutMapOutlinedIcon,
  ZoomInMapOutlined as ZoomInMapOutlinedIcon,
  ReorderOutlined as ReorderOutlinedIcon,
} from "@mui/icons-material";

// Material-UI Components
import { Button } from "@mui/material";
import { useSnackbar } from "notistack";

// Helmet for managing document head tags
import { Helmet } from "react-helmet-async";
import { MoveItemsPopup } from "./modals/MoveItemsPopup.js";
import { ReleaseItemsPopup } from "./modals/ReleaseItemsPopup.js";
import { RemoveItemsPopup } from "./modals/RemoveItemsPopup";
import AssignStoragePopup from "./modals/AssignStoragePopup.js";
import { DestroyItemsPopup } from "./modals/DestroyItemsPopup.js";
import { useDebounce, useDebouncedCallback } from "use-debounce";
import { HardDriveIcon } from "lucide-react";
import { MONOLITH_PERMISSIONS } from "../../constants.js";
import {
  ColumnProps,
  PageData,
  TableColumn,
} from "@/Monolith-UI/Table/types/Table";
import { CustomField, ItemTotalBadge, SelectedItemsBadge } from "@/Features";
import { ColumnResize, SharedCaseTabListQuery } from "../Cases/types/Cases";
import CustomFieldsApi from "@/api/custom-fields";
import { CUSTOM_FIELD_CATEGORIES } from "../Settings/CustomFieldSettings/utils/customFieldConstants";
import { safeParseJSON } from "@/utils/File-parsers/safeParseJSON";

interface ViewItemState {
  showView?: boolean;
  storage_id?: number;
}

const actions = [
  { value: 0, text: "Assign Storage Item" },
  { value: 1, text: "Move Selected Items" },
  { value: 2, text: "Release Selected Items" },
  { value: 3, text: "Remove Selected Items" },
  { value: 4, text: "Destroy Selected Items" },
];

const columnDefs = CaseStorageColumnsDefs;

const StorageSection = styled(() => {
  const { case_id } = useParams();
  const stateStoreKey =
    case_id === undefined ? "storage:view" : "case:storage:view";

  // will fix this any when we come up with the strategy to merge MUI and SC theme types
  const theme: any = useTheme();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [showMovePopup, setShowMovePopup] = useState(false);
  const [showReleasePopup, setShowReleasePopup] = useState(false);
  const [showRemovePopup, setShowRemovePopup] = useState(false);
  const [showAssignPopup, setShowAssignPopup] = useState(false);
  const [showDestroyPopup, setShowDestroyPopup] = useState(false);
  const [showViewItemModal, setShowViewItemModal] = useState(false);
  const [viewItem, setViewItem] = useState<ViewItemState | null>(null);
  const [flyoutData, setFlyoutData] = useState<StorageItem | null>(null);
  const [isFlyoutVisible, setIsFlyoutVisible] = useState(false);

  const [pageSize, setPageSize] = useState(100);
  const [query, setQuery] = useState<SharedCaseTabListQuery | undefined | null>(
    null
  );
  const [searchText, setSearchText] = useState(
    localStorage.getItem(`${stateStoreKey}:searchText`)
  );
  const [debouncedSearchText] = useDebounce(searchText, 500);

  const table = useTable();

  const [columnState, setColumnState] = useState(() => {
    return synchronizeColumnState(
      columnDefs,
      JSON.parse(localStorage.getItem(stateStoreKey) || "{}")
    );
  });

  const currentSort = useMemo(() => {
    let [sort] = columnState
      ?.filter((c) => c.sorting?.active)
      .map((c) => ({ field: c.dataField, sort: c.sorting?.direction }));

    return sort;
  }, [columnState]);

  const { data, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [
        ...(case_id === undefined
          ? storageQueryKeys.legacyLists
          : storageQueryKeys.legacyCaseLists),
        {
          query: {
            ...query?.query,
            order: query?.query?.order || currentSort,
            pageSize,
            case_id: case_id ? parseInt(case_id) : null,
          },
        },
      ],
      queryFn: ({ pageParam }) =>
        StorageAPI.query({
          query: {
            ...query?.query,
            order: query?.query?.order || currentSort,
            pageSize,
            case_id: case_id ? parseInt(case_id) : null,
            page: pageParam,
          },
        }),
      getNextPageParam: (lastPage) => {
        return lastPage.nextPage;
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage.page - 1 === 0) return null;
        return firstPage.page - 1;
      },
      initialPageParam: 1,
      enabled: !!query,
      placeholderData: (data) => data,
    });

  const records = data?.pages?.reduce<StorageItem[]>((acc, page) => {
    return [...acc, ...page.data];
  }, []);

  const totalRecords = data?.pages?.[0]?.total || 0;

  const debouncedFetchNextPage = useDebouncedCallback(() => {
    fetchNextPage();
  }, 50);

  let filterDimensions = CaseStorageDimensions;

  const { data: customFields } = useQuery({
    queryKey: ["storage:custom-fields:list"],
    queryFn: () =>
      CustomFieldsApi.getCustomFields({
        category: CUSTOM_FIELD_CATEGORIES.STORAGE,
      }),
  });

  // update filter dimensions with custom fields
  filterDimensions = useMemo(() => {
    if (customFields) {
      //update filter dimensions
      return [
        ...CaseStorageDimensions,
        ...customFields?.map((field: CustomField) => ({
          name: field.field_name,
          field: `custom_field_${field.field_id}`,
          id: `custom_field_${field.field_id}`,
          type:
            field.editor_type === "dateBox"
              ? "date"
              : field.editor_type === "dropDown" ||
                field.editor_type === "tagBox"
              ? "string"
              : "text",
          mode:
            field.editor_type === "dateBox"
              ? "date"
              : field.editor_type === "dropDown" ||
                field.editor_type === "tagBox"
              ? "multi-select"
              : "text",
          options: field.options
            ? (typeof field.options === "string"
                ? safeParseJSON(field.options as string)
                : field.options
              ).map((o: string) => ({ option: o }))
            : null,
          selectionDisplayName: field.options ? "option" : null,
          selectionIdField: field.options ? "option" : null,
        })),
      ];
    }
    return CaseStorageDimensions;
  }, [customFields]);

  const { queryButton, queryFilter, conditions } = useQueryFilter({
    queryFilter: undefined,
    dimensions: filterDimensions.sort((a, b) => a.name.localeCompare(b.name)),
    onQuerySet: (newFilter) =>
      setQuery((q) => {
        return {
          query: {
            ...q?.query,
            ...newFilter,
            page: 1,
            case_id,
          },
        };
      }),
    stateStoring: {
      enabled: true,
      type: "localStorage",
      storageKey: stateStoreKey,
    },
  });

  const handleSort = (field: string) => {
    const savedColumn =
      columnState?.find((svc) => field === svc.dataField) || {};
    const order = !!savedColumn ? savedColumn?.sorting?.direction : null;

    let newOrder = null;

    // if sorted on a different column, reset the sort
    if (query?.query?.order?.field !== field) {
      newOrder = {
        field,
        sort: "asc",
      };
    }

    // otherwise rotate the sort options on the current column
    else {
      switch (order) {
        case "asc":
          newOrder = {
            field,
            sort: "desc",
          };
          break;
        case "desc":
          newOrder = null;
          break;
        default:
          newOrder = {
            field,
            sort: "asc",
          };
      }
    }

    let newCols = columnState?.map((c) => {
      if (c.dataField === field) {
        return {
          ...c,
          sorting: {
            active: newOrder ? true : false,
            direction: newOrder?.sort,
          },
        };
      }
      delete c.sorting;
      return c;
    });

    let newQuery = {
      query: {
        ...query?.query,
        order: newOrder,
      },
    };

    setColumnState(newCols);

    setQuery(newQuery);
  };
  const handleReload = () => {
    refetch();
  };
  const handleExportTable = () => {
    // show snackbar
    enqueueSnackbar("Exporting table...", {
      variant: "info",
    });

    StorageAPI.exportStorageList({
      query: {
        ...query?.query,
        order: query?.query?.order || currentSort,
        pageSize,
        case_id: case_id ? parseInt(case_id) : null,
      },
      type: "xlsx",
      columns: columnState
        .filter((c) => c.visible !== false)
        .sort((a, b) => a.order - b.order)
        .map((c) => {
          return { dataField: c.dataField, header: c.caption, ...c };
        }),
      date_format: getDateFormat({ isMoment: true, includeTime: true }),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      custom_date_format: getDateFormat({ isMoment: true, includeTime: false }),
    }).then((res) => {
      const { signedUrl, filename } = res;
      const el = document.createElement("a");
      el.href = signedUrl.replace(
        "http://localhost:3000",
        "http://localhost:3001"
      );
      // el.target = "_blank";
      el.download = filename;
      el.click();
      // remove snackbar
    });
  };
  const handleColumnReorder = (
    newOrder: { column: string; order: number }[]
  ) => {
    setColumnState((cs) => {
      return newOrder.map((o) => {
        return {
          ...cs.find((c) => c.dataField === o.column),
          order: o.order,
        };
      });
    });
  };
  const handleColumnResize = (e: ColumnResize) => {
    setColumnState((cs) => {
      return cs.map((c) => {
        const col = e.columns.find((col) => col.dataField === c.dataField);
        if (col) {
          return {
            ...c,
            width: parseInt(col.width.replace(/px/g, "")),
          };
        }
        return c;
      });
    });
  };
  const handleColumnVisibility = (
    column: ColumnProps<StorageItem>,
    visible: boolean
  ) => {
    setColumnState((cs) => {
      return cs.map((c) => {
        if (c.dataField === column.dataField) {
          return {
            ...c,
            visible,
          };
        }
        return c;
      });
    });
  };
  const handleActionButtonClick = (rowData: StorageItem) => {
    setFlyoutData(rowData);
    setIsFlyoutVisible(true);
  };
  const handleActionSelect = (action: { text: string; value: number }) => {
    const selectionCount = table.getVirtualSelectionTotal();

    switch (action.value) {
      case 0:
        setShowAssignPopup(true);
        break;
      case 1:
        if (selectionCount === 0) {
          enqueueSnackbar("No storage items selected.", {
            variant: "warning",
          });
        } else setShowMovePopup(true);
        break;
      case 2:
        if (selectionCount === 0) {
          enqueueSnackbar("No storage items selected.", {
            variant: "warning",
          });
        } else setShowReleasePopup(true);
        break;
      case 3:
        if (selectionCount === 0) {
          enqueueSnackbar("No storage items selected.", {
            variant: "warning",
          });
        } else setShowRemovePopup(true);
        break;
      case 4:
        if (selectionCount === 0) {
          enqueueSnackbar("No storage items selected.", {
            variant: "warning",
          });
        } else setShowDestroyPopup(true);
        break;
      default:
        break;
    }
  };
  const handleClearFilters = () => queryFilter.clear();
  const handleModalAction = (data?: CreateStorageResult) => {
    if (data?.showView) {
      setViewItem(data);
      setShowViewItemModal(true);
    }
    refetch();
    // Bulk move/release/remove/destroy write storage_coc rows; the Current
    // Location tooltips cache CoC per item (disabled until hovered), so
    // invalidate — not refetch — to mark them stale for the next hover.
    queryClient.invalidateQueries({
      queryKey: storageQueryKeys.cocRecordsAll,
    });
  };
  const handleGetNextItem = (uuid: string) => {
    if (!records) return;
    const current = records.findIndex((i: StorageItem) => i.uuid === uuid);
    const nextItem = records[current + 1] || records[0];

    setFlyoutData(nextItem);
  };
  const handleGetPrevItem = (uuid: string) => {
    if (!records) return;
    const current = records.findIndex((i: StorageItem) => i.uuid === uuid);
    const prevItem = records[current - 1] || records[records.length - 1];

    setFlyoutData(prevItem);
  };
  // Detect scroll to bottom
  const handleScroll = (e: React.MouseEvent) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const pagesLength = data?.pages?.length || 0;
    if (scrollHeight - scrollTop <= clientHeight + 100 * pagesLength) {
      if (hasNextPage && !isFetchingNextPage) {
        debouncedFetchNextPage();
      }
    }
  };
  const onDelete = (deletedItem?: StorageItem) => {
    queryClient.setQueryData(
      [
        ...(case_id === undefined
          ? storageQueryKeys.legacyLists
          : storageQueryKeys.legacyCaseLists),
        {
          query: {
            ...query?.query,
            order: query?.query?.order || currentSort,
            pageSize,
            case_id: case_id ? parseInt(case_id) : null,
          },
        },
      ],
      (data: PageData<StorageItem>) => {
        if (!data) return null;
        const newPages = data?.pages.map((page) => {
          page.data =
            page?.data?.filter(
              (item: StorageItem) => item.uuid !== deletedItem?.uuid
            ) || page.data;
          return page;
        });

        return {
          ...data,
          pages: newPages,
        };
      }
    );
  };

  // Persist column state to local storage
  useEffect(() => {
    const oldStateLocalStorage = localStorage.getItem(stateStoreKey);
    let oldState = oldStateLocalStorage ? JSON.parse(oldStateLocalStorage) : {};
    localStorage.setItem(
      stateStoreKey,
      JSON.stringify({
        ...oldState,
        cols: columnState,
      })
    );
  }, [columnState, stateStoreKey]);

  useEffect(() => {
    if (!customFields) return;
    // Add custom fields to column defs
    setColumnState((cs) => {
      return addCustomFieldsToColumnDefs(customFields, cs);
    });
  }, [customFields]);

  useEffect(() => {
    setQuery((q) => ({
      query: {
        ...q?.query,
        search: debouncedSearchText ? debouncedSearchText : null,
        page: 1,
      },
    }));
  }, [debouncedSearchText]);

  // A select-all / exclusion selection means "everything matching the query
  // at selection time" — the server re-resolves it against the query sent at
  // submit. If the filters or search change while such a selection is held,
  // a later bulk action (Destroy especially, which is irreversible) would act
  // on a set the user never saw. Explicit row selections are uuid snapshots
  // and stay valid, so they are kept. Sort and page changes don't affect set
  // membership and are excluded from the comparison.
  const { order: _order, page: _page, ...membershipQuery } = query?.query || {};
  const membershipQueryKey = JSON.stringify(membershipQuery);
  useEffect(() => {
    const selectionState = table.getSelectionState();
    if (selectionState === "all" || selectionState === "someExcluded") {
      table.clearSelections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipQueryKey]);

  return (
    <>
      {!case_id && (
        <>
          <Helmet title="Storage Items" />
          <Typography variant="h3" gutterBottom display="inline">
            Storage Items
          </Typography>
        </>
      )}

      <div
        style={{
          display: "flex",
          flex: "initial",
          flexDirection: "row",
          alignContent: "center",
          alignItems: "center",
          marginBottom: 5,
          marginTop: 5,
          overflow: "hidden",
        }}
      >
        {/* New and Filter */}
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={!hasPermission(MONOLITH_PERMISSIONS.STORAGE_CREATE)}
          // Deliberate twin with Features/Storage/Tables/StorageTable.tsx: same
          // testid on purpose; the id names the "new storage" contract.
          // Don't rename one side (Testing Philosophy doc).
          data-testid="storage-new-button"
          onClick={(e) => {
            setShowCreatePopup(true);
          }}
          style={{
            minWidth: "fit-content",
            fontSize: 11,
            padding: "3px 6px",
          }}
        >
          + New Storage
        </Button>
        <div style={{ marginLeft: 10 }}>{queryButton}</div>
        <ItemTotalBadge total={totalRecords || 0} Icon={HardDriveIcon} />
        {!!case_id && (
          <>
            <SelectedItemsBadge
              selected={table?.getVirtualSelectionTotal() || 0}
            />
          </>
        )}
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignContent: "center",
            alignItems: "center",
            minWidth: "fit-content",
          }}
        >
          {/* TABLE CONTROLS */}
          {hasPermission() && !!case_id && (
            <ComboButton
              selectedItems={undefined}
              type="dropDown"
              data={actions}
              title={"More Actions"}
              showDropdownIcon={true}
              displayField="text"
              variant="outlined"
              useSelectMode={false}
              onItemSelect={(item) => handleActionSelect(item)}
              textColor={theme.palette.text.primary}
            >
              Actions
            </ComboButton>
          )}
          <ComboButton
            type="multi-select"
            data={columnState.filter((c) => c.showInColumnChooser !== false)}
            displayField="caption"
            idField={"dataField"}
            selectedItems={columnState.filter((c) => c.visible !== false)}
            variant="outlined"
            closeOnSelect={false}
            textColor={theme.palette.text.secondary}
            title={"Select Columns"}
            showSearch={true}
            dropDownTitle={() => {
              return (
                <div
                  style={{
                    margin: "5px 0px",
                    padding: 3,
                    color: theme.palette.text.secondary,
                    display: "flex",
                    alignItems: "center",
                    minWidth: 200,
                  }}
                >
                  Select Columns
                </div>
              );
            }}
            onItemDeSelect={(item) => {
              handleColumnVisibility(item, false);
            }}
            onItemSelect={(item) => {
              handleColumnVisibility(item, true);
            }}
          >
            <ViewColumnOutlinedIcon style={{ fontSize: 18 }} />
          </ComboButton>
          <ComboButton
            data={undefined}
            selectedItems={undefined}
            type="button"
            variant="outlined"
            textColor={theme.palette.text.secondary}
            title={"Export Table"}
            onClick={handleExportTable}
          >
            <FileDownloadOutlinedIcon style={{ fontSize: 18 }} />
          </ComboButton>
          <ComboButton
            data={undefined}
            selectedItems={undefined}
            type="button"
            variant="outlined"
            textColor={theme.palette.text.secondary}
            title={"Clear Filters"}
            onClick={handleClearFilters}
          >
            <FilterListOffIcon style={{ fontSize: 18 }} />
          </ComboButton>
          <ComboButton
            data={undefined}
            selectedItems={undefined}
            type="button"
            variant="outlined"
            textColor={theme.palette.text.secondary}
            title={table.isCompact ? "Zoom In" : "Zoom Out"}
            onClick={() => table.toggleCompact()}
          >
            {table.isCompact ? (
              <ZoomOutMapOutlinedIcon style={{ fontSize: 18 }} />
            ) : (
              <ZoomInMapOutlinedIcon style={{ fontSize: 18 }} />
            )}
          </ComboButton>
          <ComboButton
            data={undefined}
            selectedItems={undefined}
            type="button"
            variant={"outlined"}
            textColor={
              table.isStriped
                ? theme.palette.primary.main
                : theme.palette.text.secondary
            }
            title={table.isStriped ? "Hide Stripes" : "Show Stripes"}
            onClick={() => table.toggleStripes()}
          >
            <ReorderOutlinedIcon style={{ fontSize: 18 }} />
          </ComboButton>
          {/* SEARCH */}
          <Input
            placeholder="Search Storage"
            variant="outlined"
            height={30}
            defaultValue={searchText}
            onKeyUp={(e: React.KeyboardEvent) => {
              const target = e.target as HTMLInputElement;
              const currentValue = target.value.trim();
              setSearchText(currentValue);

              localStorage.setItem(`${stateStoreKey}:searchText`, currentValue);
            }}
            style={{
              marginLeft: "10px",
              border: searchText
                ? `1px solid ${theme.palette.primary.main}`
                : "",
            }}
          />
        </div>
      </div>
      {data && (
        <>
          {<div>{conditions}</div>}
          <Table
            data={records}
            totalRecords={totalRecords}
            tableInstance={table}
            keyValue="uuid"
            columnProps={{ minWidth: 150, width: 150 }}
            onHeaderClick={(col: TableColumn<StorageItem>) =>
              col?.sorting?.enabled === false ? null : handleSort(col.dataField)
            }
            onScroll={handleScroll}
            onColumnReorder={handleColumnReorder}
            onColumnResize={handleColumnResize}
            onActionButtonClick={handleActionButtonClick}
            showActionColumn={true}
            focusedRow={flyoutData}
            showSelection={case_id === undefined ? false : true}
            selectAll={case_id === undefined ? false : true}
          >
            {columnState.map((col) => {
              return <Column key={col.dataField} {...col} />;
            })}
          </Table>
        </>
      )}
      {!data && <Loader message={"Retrieving Items..."} />}
      {isFlyoutVisible && flyoutData && (
        <StorageFlyout
          flyoutData={flyoutData}
          setFlyoutData={setFlyoutData}
          setIsFlyoutVisible={setIsFlyoutVisible}
          handleGetNextItem={handleGetNextItem}
          handleGetPrevItem={handleGetPrevItem}
          columnState={columnState}
          handleRefresh={handleReload}
          onDelete={onDelete}
        />
      )}
      <CreateStorageModal
        show={showCreatePopup}
        onClose={() => setShowCreatePopup(false)}
        onCancel={() => setShowCreatePopup(false)}
        onSubmit={handleModalAction}
        caseInfo={{
          case_id: case_id ? parseInt(case_id) : null,
        }}
      />
      <Modal
        open={showViewItemModal}
        onClose={() => setShowViewItemModal(false)}
      >
        <Modal.Title>View Storage Item</Modal.Title>
        <div style={{ fontWeight: 500, margin: "20px 0px" }}>
          Would you like to view this new storage item?
        </div>
        <ButtonMenu>
          <MonolithButton
            variant="subtle"
            size="xs"
            onClick={() => setShowViewItemModal(false)}
          >
            No Thanks
          </MonolithButton>
          <MonolithButton
            variant="contained"
            size="xs"
            onClick={() => {
              setShowViewItemModal(false);
              navigate(`/storage/${viewItem?.storage_id}`);
            }}
          >
            View Storage Item
          </MonolithButton>
        </ButtonMenu>
      </Modal>
      {case_id !== undefined && (
        <>
          <MoveItemsPopup
            onSubmit={handleModalAction}
            caseInfo={{ case_id: case_id ? parseInt(case_id) : null }}
            open={showMovePopup}
            handleClose={() => setShowMovePopup(false)}
            table={table}
            query={query}
          />
          <ReleaseItemsPopup
            onSubmit={handleModalAction}
            caseInfo={{ case_id: case_id ? parseInt(case_id) : null }}
            open={showReleasePopup}
            handleClose={() => setShowReleasePopup(false)}
            table={table}
            query={query}
          />
          <RemoveItemsPopup
            onSubmit={handleModalAction}
            caseInfo={{ case_id: case_id ? parseInt(case_id) : null }}
            open={showRemovePopup}
            handleClose={() => setShowRemovePopup(false)}
            table={table}
            query={query}
          />
          <AssignStoragePopup
            onSubmit={handleModalAction}
            caseInfo={{ case_id: case_id ? parseInt(case_id) : null }}
            open={showAssignPopup}
            handleClose={() => setShowAssignPopup(false)}
            table={table}
          />
          <DestroyItemsPopup
            onSubmit={handleModalAction}
            caseInfo={{ case_id: case_id ? parseInt(case_id) : null }}
            open={showDestroyPopup}
            handleClose={() => setShowDestroyPopup(false)}
            table={table}
            query={query}
          />
        </>
      )}
    </>
  );
})`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

export { StorageSection };
